// Encode/decode helpers for share-link URLs.
// Bundles get JSON.stringify'd, then UTF-8-safe base64 encoded, then mapped to
// URL-safe characters (-, _, no padding). Lives in the URL hash (#data=...)
// because hashes are NOT sent to the server — keeps the project data fully
// client-side.
//
// Size note: typical 5-task project ≈ 3 KB JSON → ~4 KB base64. Some chat
// apps truncate display URLs around 2 KB but the link itself still works.
// File export is still the recommended path for very large projects.

const HASH_KEY = 'data='

/** Encode any JSON-serialisable object into a URL-safe base64 string. */
export function encodeShareData(obj: unknown): string {
  const json = JSON.stringify(obj)
  // UTF-8 safe via the classic unescape(encodeURIComponent(...)) trick — btoa
  // alone throws on non-ASCII characters (e.g. accented project names).
  const b64 = btoa(unescape(encodeURIComponent(json)))
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Decode the URL-safe base64 back to its original JSON-shaped object.
 *  Throws if the string isn't valid base64 or isn't valid JSON. */
export function decodeShareData<T = unknown>(encoded: string): T {
  let b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
  while (b64.length % 4) b64 += '='
  const json = decodeURIComponent(escape(atob(b64)))
  return JSON.parse(json) as T
}

/** Build a full https://renosheets.com/import#data=... URL from a bundle. */
export function buildShareUrl(origin: string, bundle: unknown): string {
  return `${origin}/import#${HASH_KEY}${encodeShareData(bundle)}`
}

/** Pull the encoded data out of a window.location.hash string, if present.
 *  Returns null if the hash is empty or doesn't carry the `data=` key. */
export function readShareHash(hash: string): string | null {
  // hash includes the leading '#' — strip it
  const h = hash.startsWith('#') ? hash.slice(1) : hash
  if (!h.startsWith(HASH_KEY)) return null
  const encoded = h.slice(HASH_KEY.length)
  return encoded || null
}
